import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAccountPositionStore } from '../../Store/accountPositionStore';
import { useAuth } from '../../Context/AuthProvider';
import useSnackbar from '../../Utils/useSnackbar';
import { tokens, withAlpha } from '../../Common/designTokens';

const ConfirmDeleteDialog = ({ open, onClose, account }) => {
  const { deleteAccount, isLoading, error } = useAccountPositionStore();
  const { token } = useAuth();
  const { showSuccess } = useSnackbar();
  const [localError, setLocalError] = useState('');
  
  // Handle store errors - keep them persistent
  useEffect(() => {
    if (error) {
      setLocalError(error);
    }
  }, [error]);
  
  // Clear local error when dialog closes
  useEffect(() => {
    if (!open) {
      setLocalError('');
    }
  }, [open]);
  
  const handleConfirmDelete = async () => {
    if (!account || !token) return;
    
    setLocalError(''); // Clear any existing error
    
    try {
      console.log(`Attempting to delete account:`,account);
      await deleteAccount(account.id, token);
      onClose(true);
    } catch (error) {
      // Error is already handled by store
      console.error("Delete failed:", error);
    }
  };
  
  const handleClose = () => {
    if (!isLoading) {
      setLocalError('');
      onClose(false);
    }
  };
  
  if (!account) return null;

  // Common modal style matching OrderConfirmationModal (light theme tokens)
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 450,
    bgcolor: tokens.colors.bodyBackgroundColor,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };
  
  return (
    <Modal
      open={open}
      onClose={!isLoading ? handleClose : undefined}
      aria-labelledby="confirm-account-delete-modal"
    >
      <Box sx={modalStyle}>
        <Typography id="confirm-account-delete-modal-title" variant="h6" component="h2" sx={{ color: tokens.colors.fontColour, mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
          Confirm Delete Account
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: tokens.palette.danger }} />
        </Box>
        
        {/* Display persistent error message if present */}
        {/* {localError && !localError.startsWith('Position') && (
          <Box sx={{ 
            width: '100%',
            mb: 2, 
            p: 1,
            backgroundColor: 'rgba(255, 0, 0, 0.1)', 
            border: '1px solid rgba(255, 0, 0, 0.3)',
            borderRadius: '4px'
          }}>
            <Typography sx={{ color: 'red', textAlign: 'center', fontWeight: 'bold' }}>
              {localError}
            </Typography>
          </Box>
        )} */}
        
        <Typography variant="subtitle1" sx={{ color: tokens.colors.fontColour85, textAlign: 'center', mb: 2 }}>
          Are you sure you want to delete this account?<br /> <b>ALL</b> associated positions will also be deleted.
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 1, color: tokens.colors.fontColour, textAlign: 'center' }}>
          Account: {account.description || 'Unknown'}
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 1, color: tokens.colors.fontColour, textAlign: 'center' }}>
          Account Number: {account.accountNumber || account.accountIdentifier || 'Unknown'}
        </Typography>
        
        <Typography variant="body2" sx={{ color: tokens.palette.danger, mb: 3, textAlign: 'center', fontWeight: 600 }}>
          This action cannot be undone.
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => onClose(false)}
            disabled={isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 700,
             
              width: 120,
              borderRadius: '8px',
              
              '&.Mui-disabled': {
                color: tokens.colors.disabledFontColour,
                borderColor: tokens.colors.bodyBorderColor50,
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleConfirmDelete}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              width: 140,
              color: tokens.colors.white,
              borderColor: tokens.palette.danger,
              backgroundColor: withAlpha(tokens.palette.danger, 0.95),
              borderRadius: '8px',
              '&:hover': { backgroundColor: withAlpha(tokens.palette.danger, 1) },
              '&.Mui-disabled': {
                color: withAlpha(tokens.colors.white, 0.4),
                borderColor: withAlpha(tokens.colors.white, 0.4),
              }
            }}
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmDeleteDialog;
